"use client";

import React, { useRef, useState, useCallback, useMemo } from "react";
import {
  ActionIcon,
  Pill,
  PillsInput,
  ScrollArea,
  UnstyledButton,
  Box as MantineBox,
  List as MantineList,
  Text as MantineText,
} from "@mantine/core";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { sizeOptions, footwearJpToEu } from "@/lib/shared/size";

import Modal from "@/components/Modal";
import SizeTable from "@/components/SizeTable";
import InfoIcon from "@/icons/info";
import DropdownChevron from "@/icons/DropdownChevron";

import styles from "./styles.module.scss";

type Props = {
  icon: React.ReactNode;
  activeFilters: Filter[];
  selectedTypes: Filter[];
  onSelect: (type: string, value: Filter[]) => void;
  disabled?: boolean;
};

const getDisplaySize = (categoryValue: string, size: string) => {
  if (categoryValue === "footwear" && footwearJpToEu[size]) {
    return footwearJpToEu[size];
  }
  return size;
};

const SizeDropdown = ({ activeFilters = [], onSelect, disabled, selectedTypes }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState<string>();
  const [openModal, setOpenModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [, setFocusedIndex] = useState(-1);
  const dropdownMaxHeight = isOpen ? "18rem" : "0rem";

  useOnClickOutside(wrapperRef, () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  });

  const filteredSizes = useMemo(() => {
    if (!selectedTypes?.length) {
      return sizeOptions;
    }

    const clothingTypes = sizeOptions.map((option) => option.value);

    const types = selectedTypes
      .filter((option) => option?.state !== "exclude")
      .map((option) => option.value)
      .filter((type) => clothingTypes.includes(String(type) || ""));

    const otherTypes = selectedTypes.filter((selected) => !types.includes(selected.value));

    if (!types.length && otherTypes.length) return sizeOptions;

    return sizeOptions.filter((option) => types.includes(option.value));
  }, [selectedTypes]);

  const isSelected = useCallback(
    (option: { id: number; label: string; value: string; options: string[] }, size: string) => {
      return !!activeFilters?.some(
        (selected) => selected.value === option.value && selected.size === size
      );
    },
    [activeFilters]
  );

  const toggleShowAll = (option: {
    id: number;
    label: string;
    value: string;
    options: string[];
  }) => {
    setShowAll((prev) => (prev === String(option.value) ? "" : String(option.value)));
  };

  const withPreventDefault = (handler: () => void) => {
    return function (event: React.SyntheticEvent) {
      event.preventDefault();
      event.stopPropagation();
      handler();
    };
  };

  // Get all focusable buttons (size buttons + more/less buttons)
  const getAllSizeButtons = useCallback(() => {
    if (!wrapperRef.current) return [];

    return Array.from(
      wrapperRef.current.querySelectorAll("[data-size-button], [data-expand-button]")
    ) as HTMLButtonElement[];
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;

      const buttons = getAllSizeButtons();

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev < buttons.length - 1 ? prev + 1 : 0;
            buttons[newIndex]?.focus();
            return newIndex;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : buttons.length - 1;
            buttons[newIndex]?.focus();
            return newIndex;
          });
          break;

        case "ArrowRight":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev < buttons.length - 1 ? prev + 1 : prev;
            buttons[newIndex]?.focus();
            return newIndex;
          });
          break;

        case "ArrowLeft":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : prev;
            buttons[newIndex]?.focus();
            return newIndex;
          });
          break;

        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          // Focus back to the trigger
          const trigger = wrapperRef.current?.querySelector('[tabindex="0"]') as HTMLElement;
          trigger?.focus();
          break;

        case "Home":
          e.preventDefault();
          setFocusedIndex(0);
          buttons[0]?.focus();
          break;

        case "End":
          e.preventDefault();
          const lastIndex = buttons.length - 1;
          setFocusedIndex(lastIndex);
          buttons[lastIndex]?.focus();
          break;

        case "Tab":
          // Close dropdown when tabbing out
          // Don't prevent default to allow natural tab flow
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
      }
    },
    [isOpen, getAllSizeButtons]
  );

  const toggleModal = () => {
    setOpenModal((prev) => !prev);
  };

  const handleOptionClick = (option: Filter, size: string) => {
    const selectedSet = new Set(activeFilters?.map((opt) => `${opt.value}-${opt.size}`));

    if (selectedSet.has(`${option.value}-${size}`)) {
      const newSelected = activeFilters?.filter(
        (selected) => selected.value !== option.value || selected.size !== size
      );
      onSelect("size", newSelected);
    } else {
      onSelect("size", [
        ...(activeFilters || []),
        {
          id: parseInt(`${option.value}${size.charCodeAt(0)}`, 10),
          type: "size",
          label: option.label,
          value: option.value,
          size,
          included: true,
        },
      ]);
    }
  };

  return (
    <>
      <Modal
        isOpen={openModal}
        closeModal={() => setOpenModal(false)}
        zIndex={9000}
        styles={{
          content: {
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          },
          body: {
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
          },
        }}
      >
        <SizeTable />
      </Modal>

      <MantineBox className={styles.dropdownWrapper} ref={wrapperRef} data-disabled={disabled}>
        <MantineBox className={styles.labelRow}>
          <MantineBox component="label" id="size-dropdown-label" className={styles.label}>
            SIZE
          </MantineBox>
          <ActionIcon
            className={styles.infoButton}
            onClick={(e) => {
              e.stopPropagation();
              toggleModal();
            }}
            aria-label="Open size chart"
            variant="transparent"
            size="sm"
          >
            <InfoIcon />
          </ActionIcon>
        </MantineBox>

        <PillsInput
          className={styles.dropdownHeader}
          data-open={isOpen}
          tabIndex={0}
          aria-expanded={isOpen}
          aria-controls="size-options-list"
          aria-haspopup="listbox"
          aria-labelledby="size-dropdown-label"
          rightSection={<DropdownChevron />}
          rightSectionWidth={40}
          wrapperProps={{
            onClick: () => {
              setIsOpen((prev) => {
                const next = !prev;
                if (next && wrapperRef.current) {
                  const rect = wrapperRef.current.getBoundingClientRect();
                  const spaceBelow = window.innerHeight - rect.bottom;
                  setOpenUpward(spaceBelow < 290);
                }
                return next;
              });
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsOpen((prev) => !prev);
              } else if (e.key === "ArrowDown" && !isOpen) {
                e.preventDefault();
                setIsOpen(true);
                // Focus first size button after opening
                setTimeout(() => {
                  const firstButton = wrapperRef.current?.querySelector(
                    "[data-size-button]"
                  ) as HTMLButtonElement;
                  if (firstButton) {
                    firstButton.focus();
                    setFocusedIndex(0);
                  }
                }, 0);
              } else if (isOpen) {
                // Pass through keyboard events to the dropdown when open
                handleKeyDown(e);
              }
            },
          }}
        >
          <Pill.Group>
            {activeFilters?.map((f) => (
              <Pill
                key={`${f.value}-${f.size}`}
                withRemoveButton
                onRemove={() => {
                  onSelect(
                    "size",
                    activeFilters.filter((s) => !(s.value === f.value && s.size === f.size))
                  );
                }}
              >
                {f.label} - {getDisplaySize(String(f.value), f.size || "")}
              </Pill>
            ))}
          </Pill.Group>
        </PillsInput>
        <MantineList
          id="size-options-list"
          className={`${styles.dropdown} ${isOpen ? styles.open : ""} ${openUpward ? styles.openUpward : ""}`}
          role="listbox"
          aria-multiselectable="true"
          onKeyDown={handleKeyDown}
        >
          <ScrollArea h={dropdownMaxHeight}>
            {filteredSizes?.map((option, i) => {
              const start = Math.max(0, Math.floor((option.options.length - 8) / 2));
              const end = start + 8;
              const isExpanded = showAll === option.value;

              return (
                <MantineList.Item className={styles.sizeGroup} key={`${option.label}-${i}`}>
                  <MantineBox className={styles.title}>
                    <MantineText id={`group-label-${option.value}`}>{option.label}</MantineText>
                    <ActionIcon
                      className={styles.infoButton}
                      onClick={withPreventDefault(toggleModal)}
                      aria-label={`Open size chart for ${option.label}`}
                      variant="transparent"
                      size="sm"
                    >
                      <InfoIcon />
                    </ActionIcon>
                  </MantineBox>
                  <MantineBox
                    className={styles.buttonContainer}
                    role="group"
                    aria-labelledby={`group-label-${option.value}`}
                  >
                    {(isExpanded ? option.options : option.options.slice(start, end))?.map(
                      (size, i) => (
                        <UnstyledButton
                          role="option"
                          aria-selected={isSelected(option, size)}
                          className={styles.sizeButton}
                          onClick={withPreventDefault(() => handleOptionClick(option, size))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOptionClick(option, size);
                            }
                          }}
                          data-active={isSelected(option, size)}
                          data-size-button
                          tabIndex={-1}
                          key={`${option.label}-${size}-${i}`}
                        >
                          {getDisplaySize(option.value, size)}
                        </UnstyledButton>
                      )
                    )}
                    <UnstyledButton
                      className={styles.expand}
                      onClick={withPreventDefault(() => toggleShowAll(option))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleShowAll(option);
                        }
                      }}
                      aria-label={`${isExpanded ? "Show fewer" : "Show more"} sizes for ${option.label}`}
                      data-expand-button
                      tabIndex={-1}
                    >
                      {isExpanded ? "LESS" : "MORE"}
                    </UnstyledButton>
                  </MantineBox>
                </MantineList.Item>
              );
            })}
          </ScrollArea>
        </MantineList>
      </MantineBox>
    </>
  );
};

export default SizeDropdown;
